import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const SUBBYSWAP_URL = 'https://subbyswap.app';

const LEAGUES = [
  { name: 'Incubadora', range: '0 – 100' },
  { name: 'Bronce', range: '101 – 500' },
  { name: 'Plata', range: '501 – 2.000' },
  { name: 'Oro', range: '2.001 – 10.000' },
  { name: 'Platino', range: '10.001 – 50.000' },
  { name: 'TOP1', range: '50.001+' },
] as const;

const STEPS = [
  {
    title: 'Reclamas tu newsletter',
    body: 'Pegas la URL de tu publicación (*.substack.com o dominio propio). SubbySwap extrae nombre, foto, descripción, temática y una estimación de suscriptores / liga. Si no puede detectar el tamaño, tú eliges la liga.',
  },
  {
    title: 'Descubres partners de tu liga',
    body: 'Buscas y filtras por tamaño (liga) y categoría / temática. Los Embajadores aparecen primero. Encuentras gente con la que un intercambio tiene sentido.',
  },
  {
    title: 'Acordáis el swap',
    body: 'Solicitas un intercambio de N días (por defecto 30). La otra parte acepta. Ambos activáis la recomendación en Substack. Cuando el sistema ve ambas públicas, el contrato pasa a activo.',
  },
  {
    title: 'Verificación diaria',
    body: 'Un bot inspecciona cada día las páginas públicas de recomendaciones. Si alguien retira la suya antes de tiempo, el swap se marca como roto y se penaliza el Karma Score. Sin contraseña de Substack: solo datos públicos.',
  },
  {
    title: 'Gestionas y te enteras',
    body: 'Ves contratos vigentes y cerrados en tu cuenta. Recibes emails al inicio y al final. Si algo va mal, puedes reportarlo.',
  },
] as const;

const FEATURES = [
  {
    title: 'Partners reales, no chats eternos',
    body: 'Matchmaking por liga y temática. Menos DMs fríos y grupos dispersos.',
  },
  {
    title: 'Acuerdo claro desde el minuto uno',
    body: 'Duración definida y estados visibles: pendiente, activo, roto, cerrado.',
  },
  {
    title: 'Verificación automática',
    body: 'El árbitro mira la lista pública de recomendaciones. Si está, está. Si desapareció, se nota.',
  },
  {
    title: 'Incentivos para cumplir',
    body: 'Karma Score e insignia de Embajador: ser fiable tiene recompensa visible.',
  },
  {
    title: 'Más justo para quien empieza',
    body: 'La Incubadora protege a newsletters de 0–100: solo intercambian entre sí.',
  },
  {
    title: 'Privacidad sensata',
    body: 'Sin contraseña ni tokens de Substack. Login con magic link. Solo datos públicos.',
  },
] as const;

const NOT_THIS = [
  'No es un marketplace de spam ni de “compra suscriptores”.',
  'No es otra red social generalista.',
  'No pide ni almacena credenciales de Substack.',
  'No garantiza crecimiento mágico: garantiza un marco de confianza; el resultado editorial sigue dependiendo de la calidad y el encaje de audiencias.',
  'No sustituye escribir un buen newsletter: complementa la distribución entre creadores.',
] as const;

const COMPARE = [
  { without: 'Buscar partners a mano', with: 'Matchmaking por liga y temática' },
  { without: 'Acuerdo en un chat', with: 'Contrato con duración y estados' },
  { without: 'Confiar en capturas / palabras', with: 'Verificación diaria de datos públicos' },
  { without: 'Cumplir sin consecuencias visibles', with: 'Karma Score + Embajadores' },
  { without: 'Recordatorios manuales', with: 'Emails al inicio y fin' },
  { without: 'Desequilibrio de tamaños', with: 'Ligas (Incubadora protegida)' },
] as const;

@customElement('subbyswap-page')
export class SubbyswapPage extends LitElement {
  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .hero {
        position: relative;
        margin-bottom: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        overflow: hidden;
        text-align: center;
      }

      .hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -10%;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: var(--glow-cyan);
        filter: blur(50px);
        pointer-events: none;
      }

      .hero-content {
        position: relative;
        z-index: 1;
      }

      .eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: var(--space-md);
        padding: 0.25rem 0.75rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-accent);
      }

      h1 {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .brand-name {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .tagline {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--color-primary-light);
        margin: 0 0 var(--space-md);
      }

      .hero-lead {
        font-size: 1.125rem;
        color: var(--color-text-muted);
        max-width: 52ch;
        margin: 0 auto var(--space-xl);
        line-height: 1.6;
      }

      .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        justify-content: center;
      }

      section {
        margin-bottom: var(--space-2xl);
      }

      .section-title {
        font-size: clamp(1.5rem, 3vw, 1.875rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-sm);
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1rem;
        margin: 0 0 var(--space-xl);
        max-width: 56ch;
        line-height: 1.6;
      }

      .problem-list {
        display: grid;
        gap: var(--space-md);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .problem-list li {
        padding: var(--space-lg);
      }

      .problem-list h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .problem-list p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .flow-steps {
        display: grid;
        gap: var(--space-md);
      }

      .step {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .step-num {
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-weight: 700;
        font-size: 0.875rem;
      }

      .step h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
      }

      .step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .leagues {
        display: grid;
        gap: var(--space-sm);
        grid-template-columns: repeat(2, 1fr);
        margin-bottom: var(--space-lg);
      }

      @media (min-width: 700px) {
        .leagues {
          grid-template-columns: repeat(3, 1fr);
        }

        .features {
          grid-template-columns: repeat(2, 1fr);
        }

        .compare {
          grid-template-columns: 1fr 1fr;
        }
      }

      .league {
        text-align: center;
        padding: var(--space-md);
      }

      .league-name {
        display: block;
        font-weight: 600;
        font-size: 0.9375rem;
        margin-bottom: 0.25rem;
      }

      .league-range {
        display: block;
        font-size: 0.8125rem;
        color: var(--color-text-muted);
      }

      .rules {
        margin: 0;
        padding-left: 1.25rem;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.65;
      }

      .rules li + li {
        margin-top: var(--space-xs);
      }

      .karma-card {
        padding: var(--space-xl);
      }

      .karma-formula {
        font-family: var(--font-mono);
        font-size: 0.9375rem;
        color: var(--color-accent);
        margin: 0 0 var(--space-md);
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        background: rgb(255 255 255 / 4%);
        border: 1px solid var(--color-border);
        display: inline-block;
      }

      .karma-card p {
        margin: 0 0 var(--space-sm);
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .karma-card p:last-child {
        margin-bottom: 0;
      }

      .features {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      .feature h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .feature p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .not-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: var(--space-sm);
      }

      .not-list li {
        padding: var(--space-md) var(--space-lg);
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .compare {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      .compare-col h3 {
        margin: 0 0 var(--space-md);
        font-size: 1rem;
      }

      .compare-col ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: var(--space-sm);
      }

      .compare-col li {
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.5;
        padding-left: 1rem;
        position: relative;
      }

      .compare-col--without li::before {
        content: '–';
        position: absolute;
        left: 0;
        color: var(--color-text-subtle);
      }

      .compare-col--with li::before {
        content: '+';
        position: absolute;
        left: 0;
        color: var(--color-accent);
        font-weight: 600;
      }

      .cta-block {
        margin-top: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        text-align: center;
      }

      .cta-block h2 {
        margin: 0 0 var(--space-sm);
        font-size: clamp(1.375rem, 3vw, 1.75rem);
      }

      .cta-block p {
        margin: 0 auto var(--space-xl);
        max-width: 48ch;
        color: var(--color-text-muted);
        font-size: 1rem;
        line-height: 1.6;
      }
    `,
  ];

  render() {
    return html`
      <section class="hero animate-fade-up">
        <div class="hero-content">
          <span class="eyebrow">Proyecto de AlejandroSN</span>
          <h1><span class="brand-name">SubbySwap</span></h1>
          <p class="tagline">Swaps con prueba, no con fe.</p>
          <p class="hero-lead">
            SubbySwap es un árbitro automático de intercambios de recomendaciones entre creadores
            de Substack. Encuentras partners de tu tamaño y temática, acordáis recomendaros
            mutuamente y la herramienta comprueba cada día que ambos cumplen el trato.
          </p>
          <div class="hero-actions">
            <ui-button href=${SUBBYSWAP_URL} variant="primary">Unirme a la lista de espera</ui-button>
          </div>
        </div>
      </section>

      <section>
        <h2 class="section-title">El problema que resuelve</h2>
        <p class="section-lead">
          En Substack, las recomendaciones mutuas son una forma natural de crecer. En la práctica
          suelen fallar. SubbySwap convierte ese caos en un contrato corto, claro y verificable.
        </p>
        <ul class="problem-list">
          <li class="card">
            <h3>Cuesta encontrar a la persona adecuada</h3>
            <p>
              Necesitas tamaño similar, tema compatible y ganas reales de cumplir. Los DMs fríos,
              Discord y hilos eternos no escalan.
            </p>
          </li>
          <li class="card">
            <h3>Los acuerdos son verbales e informales</h3>
            <p>
              “Te añado, tú me añades” vive en un chat. Sin fecha de fin, sin recordatorios, sin
              estado compartido.
            </p>
          </li>
          <li class="card">
            <h3>No hay forma fácil de saber si cumplen</h3>
            <p>
              Una captura se manipula. Un “te juro que sí” no escala. Cuando alguien retira la
              recomendación en silencio, casi nadie se entera a tiempo.
            </p>
          </li>
        </ul>
      </section>

      <section>
        <h2 class="section-title">Cómo funciona</h2>
        <p class="section-lead">De punta a punta, sin acceso a tu cuenta de Substack.</p>
        <div class="flow-steps">
          ${STEPS.map(
            (step, i) => html`
              <div class="step">
                <span class="step-num">${i + 1}</span>
                <div>
                  <h3>${step.title}</h3>
                  <p>${step.body}</p>
                </div>
              </div>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Las ligas</h2>
        <p class="section-lead">
          Un newsletter de 80 suscriptores no debería competir por atención con uno de 8.000.
          SubbySwap organiza a los creadores por tamaño aproximado.
        </p>
        <div class="leagues">
          ${LEAGUES.map(
            (league) => html`
              <article class="card league">
                <span class="league-name">${league.name}</span>
                <span class="league-range">${league.range} suscriptores</span>
              </article>
            `,
          )}
        </div>
        <ul class="rules">
          <li>La <strong>Incubadora solo intercambia entre sí</strong>, para proteger a quien empieza.</li>
          <li>
            En el resto, el matchmaking busca partners de <strong>tu liga</strong> (y adyacentes
            cuando tiene sentido).
          </li>
          <li>
            El tamaño es solo una dimensión: <strong>la temática también importa</strong>. El
            filtro por categoría evita ruido.
          </li>
        </ul>
      </section>

      <section>
        <h2 class="section-title">Karma Score e insignia de Embajador</h2>
        <p class="section-lead">
          La confianza en internet no escala con “confía en mí”. SubbySwap convierte el historial
          de cumplimiento en un número.
        </p>
        <div class="card karma-card">
          <p class="karma-formula">Karma Score = (días cumplidos / días acordados) × 100</p>
          <p>Cumplir sube (o mantiene) tu reputación. Romper un trato se nota: el contrato queda marcado y el karma se penaliza.</p>
          <p>
            Con <strong>Karma ≥ 95%</strong> obtienes la insignia de <strong>Embajador</strong> y
            prioridad en el listado cuando otros buscan con quién hacer un swap.
          </p>
        </div>
      </section>

      <section>
        <h2 class="section-title">Ventajas concretas</h2>
        <p class="section-lead">Menos burocracia improvisada. Más tiempo para escribir.</p>
        <div class="features">
          ${FEATURES.map(
            (f) => html`
              <article class="card feature">
                <h3>${f.title}</h3>
                <p>${f.body}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Qué no es SubbySwap</h2>
        <p class="section-lead">Para evitar expectativas equivocadas.</p>
        <ul class="not-list">
          ${NOT_THIS.map((item) => html`<li class="card">${item}</li>`)}
        </ul>
      </section>

      <section>
        <h2 class="section-title">Sin SubbySwap vs con SubbySwap</h2>
        <div class="compare">
          <div class="card compare-col compare-col--without">
            <h3>Sin SubbySwap</h3>
            <ul>
              ${COMPARE.map((row) => html`<li>${row.without}</li>`)}
            </ul>
          </div>
          <div class="card compare-col compare-col--with">
            <h3>Con SubbySwap</h3>
            <ul>
              ${COMPARE.map((row) => html`<li>${row.with}</li>`)}
            </ul>
          </div>
        </div>
      </section>

      <div class="cta-block animate-fade-up">
        <h2>Beta / lista de espera</h2>
        <p>
          SubbySwap está en beta. La landing recoge emails para avisar cuando las cuentas se
          abran. Al lanzar, el acceso está pensado como gratis. Si eres creador de Substack —
          sobre todo pequeño o mediano — y te importa crecer sin trucos, ahí es el sitio.
        </p>
        <ui-button href=${SUBBYSWAP_URL} variant="primary">Ir a subbyswap.app</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'subbyswap-page': SubbyswapPage;
  }
}
